import React from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Pin, PinOff, Trash2 } from 'lucide-react';

export type ChatActionItem = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
};

type Props = {
  /** Bekommt den Offen-Zustand, damit ein Trigger z. B. ein Chevron drehen kann. */
  renderTrigger: (open: boolean) => React.ReactNode;
  triggerClassName?: string;
  /** Dient gleichzeitig als `aria-label` des Triggers und des Menüs. */
  label?: string;
  onRename: () => void;
  onDelete: () => void;
  isPinned?: boolean;
  onTogglePin?: () => void;
  extraItems?: ChatActionItem[];
  /** Panel-Kante, die am Trigger ausgerichtet wird. */
  align?: 'left' | 'right';
  onOpenChange?: (open: boolean) => void;
};

/** Muss zur Panel-Breite unten (`w-44`) passen — die Ausrichtung rechnet damit. */
const PANEL_WIDTH_PX = 208;
const VIEWPORT_MARGIN_PX = 8;

/** Schriftgröße und Zeilenhöhe wie in der Chatliste (`SIDEBAR_TEXT_CLASS`). */
const ITEM_CLASS = 'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[13px] focus:outline-none';

/** Anheften/Umbenennen/Löschen für einen Chat — in der Kopfzeile und in jeder Zeile der Chatliste.
 *  Das Panel hängt an `document.body`, damit weder das `overflow` der Chatliste noch
 *  ein z-index der Kopfzeile es abschneidet. */
export function ChatActionsMenu({
  renderTrigger,
  triggerClassName = '',
  label = 'Chat-Aktionen',
  onRename,
  onDelete,
  isPinned = false,
  onTogglePin,
  extraItems = [],
  align = 'left',
  onOpenChange,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  React.useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const preferredLeft = align === 'right' ? rect.right - PANEL_WIDTH_PX : rect.left;
      const maxLeft = window.innerWidth - PANEL_WIDTH_PX - VIEWPORT_MARGIN_PX;
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(VIEWPORT_MARGIN_PX, Math.min(preferredLeft, maxLeft)),
      });
    };
    update();
    window.addEventListener('resize', update);
    // Capture-Phase, damit auch das Scrollen der Chatliste das Panel mitnimmt.
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, align]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      changeOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  React.useEffect(() => {
    if (!open || !position) return;
    panelRef.current?.querySelector<HTMLButtonElement>('button[role="menuitem"]')?.focus();
  }, [open, position]);

  const closeAndFocusTrigger = () => {
    changeOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeAndFocusTrigger();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]') ?? []);
    if (items.length === 0) return;
    const activeIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex =
      event.key === 'ArrowDown'
        ? (activeIndex + 1) % items.length
        : (activeIndex - 1 + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          changeOpen(!open);
        }}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className={triggerClassName}
      >
        {renderTrigger(open)}
      </button>
      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              aria-label={label}
              onKeyDown={onPanelKeyDown}
              style={{ top: position.top, left: position.left }}
              className="fixed z-[300] w-52 overflow-hidden rounded-lg border border-app-border bg-app-surface py-1 shadow-xl"
            >
              {onTogglePin ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    changeOpen(false);
                    onTogglePin();
                  }}
                  className={`${ITEM_CLASS} text-app-text-muted hover:bg-app-hover focus:bg-app-surface-muted`}
                >
                  {isPinned ? (
                    <PinOff className="h-3.5 w-3.5 text-app-text-subtle" />
                  ) : (
                    <Pin className="h-3.5 w-3.5 text-app-text-subtle" />
                  )}
                  {isPinned ? 'Loslösen' : 'Anheften'}
                </button>
              ) : null}
              {extraItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    changeOpen(false);
                    item.onClick();
                  }}
                  className={`${ITEM_CLASS} text-app-text-muted hover:bg-app-hover focus:bg-app-surface-muted`}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  changeOpen(false);
                  onRename();
                }}
                className={`${ITEM_CLASS} text-app-text-muted hover:bg-app-hover focus:bg-app-surface-muted`}
              >
                <Pencil className="h-3.5 w-3.5 text-app-text-subtle" />
                Umbenennen
              </button>
              <div className="my-1 border-t border-app-border" />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  changeOpen(false);
                  onDelete();
                }}
                className={`${ITEM_CLASS} text-red-600 hover:bg-red-50 focus:bg-red-50 dark:focus:bg-red-500/15`}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Löschen
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
